import { jsx, type JsxElementProps } from '@amazon/vinyl-tsx'
import type { JsxChild } from '@amazon/vinyl-tsx'
import { type NavigateOptions, navigateTo, preloadRoute } from '@/router/router'
import { asData } from '@amazon/vinyl-observable'

export type LinkProps = JsxElementProps<'a'> & {
    readonly navigateOptions?: NavigateOptions
    /**
     * Runs synchronously just before the SPA `navigateTo(...)` fires.
     * Skipped on modifier / non-primary clicks (browser default handles
     * "open in new tab" and external URLs). Callers use this to seed
     * detail-page state — see `setDetailPagePreSeed` — so the destination
     * hero paints the source tile's artwork the instant it mounts.
     * Receives the source `<a>` so callers that need shared-element
     * view-transition tagging (`prepareViewTransition`) can wire it here.
     */
    readonly beforeNavigate?: (source: HTMLAnchorElement) => void
}

export function Link(props: LinkProps, children: readonly JsxChild[]) {
    // `onclick` must stay out of `rest`: spreading it would be overwritten by the
    // navigation handler below, silently dropping the caller's handler.
    const { navigateOptions, beforeNavigate, tabIndex, onclick, ...rest } = props
    const href = asData(rest.href)
    // Build the destination's view before the click so the first visit reuses
    // the already-created (memoized) element instead of rendering on nav.
    // `preloadRoute` no-ops a null / external / non-SPA href, so warm freely.
    const warm = () => preloadRoute(href.value)
    return (
        <a
            {...rest}
            tabIndex={tabIndex ?? 0}
            onmouseover={warm}
            ontouchstart={warm}
            onfocus={warm}
            onclick={(e) => {
                // First, so a caller can `preventDefault()` to cancel navigation.
                if (typeof onclick === 'function') {
                    onclick.call(e.currentTarget as unknown as GlobalEventHandlers, e)
                }
                // Only intercept SPA navigation.
                if (href.value == null || !href.value.startsWith('/')) return
                // Ignore modifier clicks or non-left clicks
                if (
                    e.defaultPrevented ||
                    e.button !== 0 ||
                    e.metaKey ||
                    e.ctrlKey ||
                    e.shiftKey ||
                    e.altKey
                ) {
                    return
                }
                e.preventDefault()
                beforeNavigate?.(e.currentTarget as HTMLAnchorElement)
                navigateTo(href.value, navigateOptions)
            }}
        >
            {...children}
        </a>
    )
}
